import type { TaxReturnStatus, AppointmentType } from '../types';

/**
 * Get the recommended next status based on current status and appointment type
 */
export function getRecommendedNextStatus(
  currentStatus: TaxReturnStatus,
  appointmentType: AppointmentType
): TaxReturnStatus | null {
  switch (currentStatus) {
    case 'scheduled':
      if (appointmentType === 'in-person') return 'in_preparation';
      return 'documents_pending'; // virtual and drop-off need docs first

    case 'documents_pending':
      if (appointmentType === 'drop-off') return 'ready_to_prepare';
      return 'in_preparation';

    case 'ready_to_prepare':
      return 'in_preparation';

    case 'in_preparation':
      return 'in_review';

    case 'in_review':
      return 'ready_for_signature';

    case 'needs_corrections':
      return 'in_preparation';

    case 'ready_for_signature':
      return 'ready_for_efile';

    case 'waiting_for_payment':
      return 'ready_for_efile';

    case 'ready_for_efile':
      return 'e_filed';

    case 'e_filed':
      return 'completed';

    default:
      return null;
  }
}

/**
 * Get a hint/guidance message for the current status transition
 */
export function getTransitionHint(
  currentStatus: TaxReturnStatus,
  appointmentType: AppointmentType
): string {
  switch (currentStatus) {
    case 'scheduled':
      if (appointmentType === 'in-person')
        return 'After the in-person appointment, move directly to In Preparation.';
      if (appointmentType === 'virtual')
        return 'Request documents before the virtual meeting. Move to Documents Pending.';
      return 'Client will drop off documents. Move to Documents Pending.';

    case 'documents_pending':
      if (appointmentType === 'drop-off')
        return 'Once all documents are received, move to Ready to Prepare so a preparer can claim it.';
      return 'Once all required documents are received, move to In Preparation.';

    case 'ready_to_prepare':
      return 'This return is in the drop-off pool. A preparer can claim it to begin preparation.';

    case 'in_preparation':
      return 'When preparation is complete, submit for review.';

    case 'in_review':
      return 'After review, approve for signature or return for corrections.';

    case 'needs_corrections':
      return 'After corrections are made, move back to In Preparation for re-review.';

    case 'ready_for_signature':
      if (appointmentType === 'in-person')
        return 'Client signs 8879 on-site. Once signed and paid, move to Ready for E-File.';
      return 'Client must complete e-signature via Zoho Sign. If signed but unpaid, the return parks at Waiting for Payment.';

    case 'waiting_for_payment':
      return 'Signature is complete. Once payment is received (cash recorded or invoice paid), the return auto-advances to Ready for E-File.';

    case 'ready_for_efile':
      return 'Submit the return for e-filing. Move to E-Filed once submitted.';

    case 'e_filed':
      return 'Waiting for IRS acceptance. Move to Completed once accepted.';

    default:
      return '';
  }
}
